import React from 'react';
import { Button } from '../Button';
import { Input } from '../Input';
import { Field, Label } from '../Fieldset';

const ScoringSection = ({
  submission,
  scores,
  errors,
  handleScoreChange,
  handleSubmit,
  isSubmitting,
  isRevision,
  isDraft,
  loadingRubric,
  rubricData,
  rubricError,
  allGuidesExpanded,
  setAllGuidesExpanded,
  isReadOnly = false,
  aiJudgment = null,
}) => {
  const aiScoresMap = {};
  if (aiJudgment?.aspect_scores) {
    aiJudgment.aspect_scores.forEach(s => {
      aiScoresMap[s.aspect_id] = s;
    });
  }
  const handleClampedScoreChange = (id, value) => {
    if (value === '') {
      handleScoreChange(id, value);
      return;
    }
    if (!/^\d+$/.test(value)) return;
    handleScoreChange(id, String(Math.min(10, Math.max(0, Number(value)))));
  };

  const blockInvalidScoreKeys = e => {
    if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl shadow-smdd-card border border-gray-300 p-8"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-baijamjuree font-extrabold text-black">
          Evaluation Scores
        </h3>
        <div className="text-right">
          <p className="text-sm text-gray-600 font-baijamjuree">
            Category:{' '}
            <span className="font-semibold text-gray-900">
              {submission.category}
            </span>
          </p>
        </div>
      </div>

      {/* Scoring Guides Section */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => setAllGuidesExpanded(!allGuidesExpanded)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors font-baijamjuree"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={allGuidesExpanded ? 'M19 9l-7 7-7-7' : 'M9 5l7 7-7 7'}
            />
          </svg>
          {allGuidesExpanded ? 'Hide Scoring Guides' : 'Show Scoring Guides'}
        </button>

        {/* Collapsible Scoring Guides */}
        {allGuidesExpanded && rubricData?.aspects && (
          <div className="mt-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(() => {
                // First pass: collect all criteria texts to find max lengths
                const allCriteria = rubricData.aspects.map(aspect => {
                  const cleanAspectName = aspect.name
                    .replace(/^[^-]+ - /, '') // Remove "Category Name - " prefix
                    .replace(/^Aspect\s*\d*\s*[-:]?\s*/i, '')
                    .replace(/^\d+\.\s*/, '')
                    .replace(/\s+/g, ' ') // Normalize whitespace
                    .trim();

                  const g = aspect.scale_guidance ?? {};

                  return {
                    aspect,
                    cleanAspectName,
                    high:
                      g['10'] || 'Outstanding performance with clear impact',
                    medium:
                      g['5'] || 'Solid performance with room for improvement',
                    low: g['0'] || 'Below expectations, requires development',
                  };
                });

                // Find maximum character lengths for each scoring tier
                const maxLengths = {
                  high: Math.max(...allCriteria.map(c => c.high.length)),
                  medium: Math.max(...allCriteria.map(c => c.medium.length)),
                  low: Math.max(...allCriteria.map(c => c.low.length)),
                };

                // Helper function to pad text to match max length
                const padText = (text, maxLength) => {
                  if (text.length >= maxLength) return text;

                  // Calculate how many characters we need to add
                  const charsToAdd = maxLength - text.length;

                  // Add invisible spaces to pad the text
                  return text + '\u00A0'.repeat(Math.ceil(charsToAdd / 2));
                };

                // Second pass: render cards with normalized text lengths
                return allCriteria.map(
                  ({ aspect, cleanAspectName, high, medium, low }) => (
                    <div
                      key={aspect.documentId}
                      className="bg-white rounded-lg p-4 border-2 border-blue-300 min-h-[200px] flex flex-col"
                    >
                      <h5 className="text-xs font-semibold text-gray-900 font-baijamjuree mb-2 leading-tight">
                        {cleanAspectName}
                      </h5>
                      {aspect.weight != null && (
                        <div className="text-xs text-blue-600 mb-2 font-medium">
                          Weight: {aspect.weight}%
                        </div>
                      )}
                      <div className="space-y-2 flex-1">
                        {/* High Score (8-10) */}
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 w-8 h-4 bg-green-500 rounded flex items-center justify-center text-xs font-bold text-white">
                            8-10
                          </div>
                          <div className="text-xs text-gray-700 leading-tight min-h-[2.5rem] flex items-start">
                            {padText(high, maxLengths.high)}
                          </div>
                        </div>
                        {/* Medium Score (4-7) */}
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 w-8 h-4 bg-yellow-500 rounded flex items-center justify-center text-xs font-bold text-white">
                            4-7
                          </div>
                          <div className="text-xs text-gray-700 leading-tight min-h-[2.5rem] flex items-start">
                            {padText(medium, maxLengths.medium)}
                          </div>
                        </div>
                        {/* Low Score (0-3) */}
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 w-8 h-4 bg-red-500 rounded flex items-center justify-center text-xs font-bold text-white">
                            0-3
                          </div>
                          <div className="text-xs text-gray-700 leading-tight min-h-[2.5rem] flex items-start">
                            {padText(low, maxLengths.low)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Loading State for Rubric */}
      {loadingRubric && (
        <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            <p className="text-blue-700 font-baijamjuree font-medium">
              Loading evaluation criteria for {submission.category}...
            </p>
          </div>
        </div>
      )}

      {/* Rubric Error State */}
      {rubricError && (
        <div className="mb-8 p-6 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <div>
              <p className="text-red-700 font-baijamjuree font-medium mb-1">
                Unable to load evaluation criteria
              </p>
              <p className="text-red-600 font-baijamjuree text-sm">
                {rubricError}
              </p>
              <p className="text-red-600 font-baijamjuree text-sm mt-2">
                Using fallback criteria. Please contact system administrator if
                this persists.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Scoring Grid */}
      {!loadingRubric && (
        <>
          {rubricData?.aspects && rubricData.aspects.length > 0 ? (
            <>
              {/* Dynamic Aspects Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {rubricData.aspects.map(aspect => {
                  // Clean up aspect name by removing category prefix
                  const cleanAspectName = aspect.name
                    .replace(/^[^-]+ - /, '') // Remove "Category Name - " prefix
                    .replace(/\s+/g, ' ') // Normalize whitespace
                    .trim();

                  return (
                    <div
                      key={aspect.documentId}
                      className="flex flex-col min-h-[120px]"
                    >
                      <Field className="flex-1 flex flex-col">
                        <Label
                          htmlFor={aspect.documentId}
                          className="text-sm font-semibold text-gray-700 font-baijamjuree leading-tight mb-3 block"
                        >
                          {cleanAspectName}
                        </Label>

                        <div className="mt-auto">
                          <Input
                            id={aspect.documentId}
                            type="number"
                            min="0"
                            max="10"
                            value={scores[aspect.documentId || ''] || ''}
                            onChange={e =>
                              handleClampedScoreChange(
                                aspect.documentId || '',
                                e.target.value
                              )
                            }
                            onKeyDown={blockInvalidScoreKeys}
                            placeholder="0-10"
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 rounded-lg transition-all duration-200 text-gray-900 font-baijamjuree placeholder-gray-500 bg-white ${
                              errors[aspect.documentId || '']
                                ? 'border-2 border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                : 'border border-gray-300 focus:border-gray-500 focus:ring-2 focus:ring-gray-200 hover:border-gray-400'
                            }`}
                          />
                          {errors[aspect.documentId || ''] && (
                            <p className="mt-2 text-sm text-red-600 font-baijamjuree">
                              {errors[aspect.documentId || '']}
                            </p>
                          )}
                        </div>
                      </Field>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {/* Fallback to hardcoded criteria */}
              <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-yellow-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <p className="text-yellow-700 font-baijamjuree font-medium">
                    Using fallback evaluation criteria (rubric not available)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Fallback Criteria 1 */}
                <Field>
                  <Label
                    htmlFor="criteria1"
                    className="block text-sm font-semibold text-gray-700 font-baijamjuree mb-2"
                  >
                    Innovation & Creativity
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="criteria1"
                    type="number"
                    min="0"
                    max="10"
                    value={scores.criteria1 || ''}
                    onChange={e =>
                      handleClampedScoreChange('criteria1', e.target.value)
                    }
                    onKeyDown={blockInvalidScoreKeys}
                    placeholder="0-10"
                    disabled={isReadOnly}
                    className={`w-full px-4 py-3 rounded-lg transition-all duration-200 text-gray-900 font-baijamjuree placeholder-gray-500 bg-white ${
                      errors.criteria1
                        ? 'border-2 border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border border-gray-300 focus:border-gray-500 focus:ring-2 focus:ring-gray-200 hover:border-gray-400'
                    }`}
                  />
                  {errors.criteria1 && (
                    <p className="mt-2 text-sm text-red-600 font-baijamjuree">
                      {errors.criteria1}
                    </p>
                  )}
                </Field>

                {/* Fallback Criteria 2 */}
                <Field>
                  <Label
                    htmlFor="criteria2"
                    className="block text-sm font-semibold text-gray-700 font-baijamjuree mb-2"
                  >
                    Technical Excellence
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="criteria2"
                    type="number"
                    min="0"
                    max="10"
                    value={scores.criteria2 || ''}
                    onChange={e =>
                      handleClampedScoreChange('criteria2', e.target.value)
                    }
                    onKeyDown={blockInvalidScoreKeys}
                    placeholder="0-10"
                    disabled={isReadOnly}
                    className={`w-full px-4 py-3 rounded-lg transition-all duration-200 text-gray-900 font-baijamjuree placeholder-gray-500 bg-white ${
                      errors.criteria2
                        ? 'border-2 border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border border-gray-300 focus:border-gray-500 focus:ring-2 focus:ring-gray-200 hover:border-gray-400'
                    }`}
                  />
                  {errors.criteria2 && (
                    <p className="mt-2 text-sm text-red-600 font-baijamjuree">
                      {errors.criteria2}
                    </p>
                  )}
                </Field>

                {/* Fallback Criteria 3 */}
                <Field>
                  <Label
                    htmlFor="criteria3"
                    className="block text-sm font-semibold text-gray-700 font-baijamjuree mb-2"
                  >
                    Business Impact
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="criteria3"
                    type="number"
                    min="0"
                    max="10"
                    value={scores.criteria3 || ''}
                    onChange={e =>
                      handleClampedScoreChange('criteria3', e.target.value)
                    }
                    onKeyDown={blockInvalidScoreKeys}
                    placeholder="0-10"
                    disabled={isReadOnly}
                    className={`w-full px-4 py-3 rounded-lg transition-all duration-200 text-gray-900 font-baijamjuree placeholder-gray-500 bg-white ${
                      errors.criteria3
                        ? 'border-2 border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border border-gray-300 focus:border-gray-500 focus:ring-2 focus:ring-gray-200 hover:border-gray-400'
                    }`}
                  />
                  {errors.criteria3 && (
                    <p className="mt-2 text-sm text-red-600 font-baijamjuree">
                      {errors.criteria3}
                    </p>
                  )}
                </Field>
              </div>
            </>
          )}
        </>
      )}

      {/* Submit Button */}
      <div className="border-t border-gray-200 pt-6 mt-8">
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="text-center sm:text-left">
            <p className="text-sm text-gray-600 font-baijamjuree mb-2">
              {rubricData?.aspects
                ? `Please review all information and provide scores for all ${rubricData.aspects.length} criteria before submitting`
                : 'Please review all information and provide scores for all three criteria before submitting'}
            </p>
            {!isRevision && (
              <p className="text-xs text-blue-600 font-baijamjuree">
                💡 This will be saved as a draft. You can revise scores later
                and submit all evaluations together.
              </p>
            )}
            {isRevision && isDraft && (
              <p className="text-xs text-yellow-600 font-baijamjuree">
                📝 Updating your draft evaluation. Changes will be saved
                immediately.
              </p>
            )}
          </div>
          <Button
            type="submit"
            disabled={isSubmitting || loadingRubric || isReadOnly}
            className={`px-8 py-3 rounded-full font-baijamjuree font-semibold text-sm transition-all duration-200 ${
              isSubmitting || loadingRubric
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-black text-white hover:bg-gray-800 hover:shadow-lg transform hover:scale-105'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                {isRevision ? 'Updating...' : 'Saving Draft...'}
              </div>
            ) : isRevision ? (
              'Update Evaluation'
            ) : (
              'Save as Draft'
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ScoringSection;
